import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRange } from '../../context/RangeContext/RangeContext';
import { SelectionGroup } from '../../components/common/SelectionGroup/SelectionGroup';
import { ButtonLarge } from '../../components/common/buttons/ButtonLarge/ButtonLarge';

import './quiz-config.css';

// Constants
const VALID_BET_TYPES = {
  OPEN: 'OPEN',
  FACING_OPEN_CALL: 'FACING_OPEN_CALL',
  FACING_OPEN_3BET: 'FACING_OPEN_3BET',
  FACING_3BET_CALL: 'FACING_3BET_CALL',
  FACING_3BET_4BET: 'FACING_3BET_4BET',
  SB_LIMP: 'SB_LIMP'
};

const POSITION_RULES = {
  'LJ': { 
    canOpen: true,
    canCallOpens: [], // LJ can't call opens, only 3bet or fold
    can3bet: [],  
    can4bet: ['SB', 'BB'],
    canCall3Bets: ['HJ', 'CO', 'BTN', 'SB', 'BB'] // LJ can call 3bets from later positions
  },
  'HJ': {
    canOpen: true,
    canCallOpens: [], // HJ can't call opens
    can3bet: ['LJ'],
    can4bet: ['SB', 'BB'],
    canCall3Bets: ['CO', 'BTN', 'SB', 'BB'] // HJ can call 3bets from later positions
  },
  'CO': {
    canOpen: true,
    canCallOpens: [], // CO can't call opens
    can3bet: ['LJ', 'HJ'],
    can4bet: ['SB', 'BB'],
    canCall3Bets: ['BTN', 'SB', 'BB'] // CO can call 3bets from later positions
  },
  'BTN': {
    canOpen: true,
    canCallOpens: ['LJ', 'HJ', 'CO'], // BTN can call opens from earlier positions
    can3bet: ['LJ', 'HJ', 'CO'],
    can4bet: ['SB', 'BB'],
    canCall3Bets: ['SB', 'BB'] // BTN can call 3bets from later positions
  },
  'SB': {
    canOpen: true,
    canCallOpens: ['LJ', 'HJ', 'CO'], // SB can't call BTN opens
    can3bet: ['LJ', 'HJ', 'CO', 'BTN'],
    can4bet: ['BB'],
    canCall3Bets: ['BB'] // SB can call 3bets from BB
  },
  'BB': {
    canOpen: false,
    canCallOpens: ['LJ', 'HJ', 'CO', 'BTN', 'SB'], // BB can call opens from all positions
    can3bet: ['LJ', 'HJ', 'CO', 'BTN', 'SB'],
    can4bet: [],
    canCall3Bets: ['SB'] // BB can call 3bets from SB
  }
};

const GAME_TYPES = ['Pre Flop', 'Post Flop'];
const ALL_POSITIONS = ['LJ', 'HJ', 'CO', 'BTN', 'SB', 'BB'];
const STREETS = ['Flop', 'Turn', 'River'];
const ACTIONS = ['SRP', '3bet'];

const POSITION_ORDER = {
  'LJ': 0,
  'HJ': 1,
  'CO': 2,
  'BTN': 3,
  'SB': 4,
  'BB': 5
};

// Utility Functions
const validateBetType = (position, betType) => {
  if (position === 'BB' && betType === 'Open') {
    console.log('Invalid bet type: BB cannot open');
    return false;
  }
  return true;
};

const getBetTypes = (position) => {
  if (!position) return [];
  
  const BET_TYPES = {
    OPEN: 'Open',
    CALL: 'Call',
    THREE_BET: '3bet',
    FOUR_BET: '4bet'
  };

  switch(position) {
    case 'BB':
      return [BET_TYPES.CALL, BET_TYPES.THREE_BET];
    case 'SB':
      return [BET_TYPES.OPEN, BET_TYPES.CALL, BET_TYPES.THREE_BET, BET_TYPES.FOUR_BET];
    case 'LJ':
      return [BET_TYPES.OPEN, BET_TYPES.CALL, BET_TYPES.FOUR_BET];
    default:
      return [BET_TYPES.OPEN, BET_TYPES.CALL, BET_TYPES.THREE_BET, BET_TYPES.FOUR_BET];
  }
};

const getValidCallTypes = (position, openPosition) => {
  if (!position) return [];
  
  // If openPosition is provided, we're in the process of configuring
  if (openPosition) {
    const heroIndex = POSITION_ORDER[position];
    const openIndex = POSITION_ORDER[openPosition];
    
    // When hero is directly to the right of opener, only vs 3bet is available
    if (heroIndex === openIndex - 1) {
      return ['vs 3bet'];
    }
    
    // For LJ, allow vs 3bet regardless of openPosition
    if (position === 'LJ') {
      return ['vs 3bet'];
    }
    
    return ['vs Open'];
  }
  
  // Special handling for SB position - change 'vs 3bet' to 'vs BB 3bet'
  if (position === 'SB') {
    return ['vs Open', 'vs BB 3bet', 'Limp'];
  }
  
  // LJ, HJ, CO only call 3bets
  if (['LJ', 'HJ', 'CO'].includes(position)) {
    return ['vs 3bet'];
  }
  
  // BTN and BB can call both opens and 3bets
  return ['vs Open', 'vs 3bet'];
};

const getValidOpenPositions = (heroPosition, betType, callType) => {
  if (!heroPosition || !betType) return [];
  
  // First check if hero position has rules defined
  if (!POSITION_RULES[heroPosition]) {
    console.error(`Invalid hero position: ${heroPosition}`);
    return [];
  }
  
  // Now handle each bet type appropriately
  switch(betType) {
    case 'Call':
      // For calls, check if it's vs open or vs 3bet
      if (callType === 'vs Open') {
        return POSITION_RULES[heroPosition].canCallOpens;
      } else if (callType === 'vs 3bet') {
        return POSITION_RULES[heroPosition].canCall3Bets;
      }
      return [];
      
    case '3bet':
      return POSITION_RULES[heroPosition].can3bet;
      
    case '4bet':
      return POSITION_RULES[heroPosition].can4bet;
      
    default:
      return [];
  }
};

const getRangePathMapping = (position, betType, callType, openPosition, threeBetPosition) => {
  if (!position || !betType) {
    console.error('Missing required params:', { position, betType });
    return null;
  }

  console.log('getRangePathMapping called with:', { 
    position, 
    betType, 
    callType, 
    openPosition, 
    threeBetPosition 
  });

  const pos = position.toLowerCase();
  const baseFolder = '/database/preflop/ranges';

  // Handle SB limp scenario
  if (betType === 'SB_LIMP' || (betType === VALID_BET_TYPES.SB_LIMP)) {
    return `${baseFolder}/${pos}/${pos}_limp.csv`;
  }
  
  // Handle open
  if (betType === VALID_BET_TYPES.OPEN) {
    return `${baseFolder}/${pos}/${pos}_open.csv`;
  }
  
  // Handle 3bet scenarios
  if (betType === VALID_BET_TYPES.FACING_OPEN_3BET) {
    if (!openPosition) {
      console.error('Missing openPosition for 3bet');
      return null;
    }
    const openPos = openPosition.toLowerCase();
    return `${baseFolder}/${pos}/${pos}_3bet-${openPos}_open.csv`;
  }
  
  // Handle call vs open scenarios
  if (betType === VALID_BET_TYPES.FACING_OPEN_CALL) {
    if (!openPosition) {
      console.error('Missing openPosition for call');
      return null;
    }
    const openPos = openPosition.toLowerCase();
    return `${baseFolder}/${pos}/${pos}_call-${openPos}_open.csv`;
  }
  
  // Handle call vs 3bet scenarios
  if (betType === VALID_BET_TYPES.FACING_3BET_CALL) {
    if (!openPosition && !threeBetPosition) {
      console.error('Missing 3bettor position for vs 3bet call');
      return null;
    }
    // Use threeBetPosition primarily, fall back to openPosition if needed
    const threeBetPos = (threeBetPosition || openPosition).toLowerCase();
    return `${baseFolder}/${pos}/${pos}_call-${threeBetPos}_3bet.csv`;
  }
  
  // Handle 4bet scenarios
  if (betType === VALID_BET_TYPES.FACING_3BET_4BET) {
    if (!threeBetPosition) {
      console.error('Missing threeBetPosition for 4bet scenario');
      return null;
    }
    const threeBetPos = threeBetPosition.toLowerCase();
    return `${baseFolder}/${pos}/${pos}_4bet-${threeBetPos}_3bet.csv`;
  }
  
  console.error('Unknown bet type:', betType);
  return null;
};

export const QuizConfigScreen = () => {
  const navigate = useNavigate();
  const { loadRange } = useRange();
  
  // State Management
  const [displayRandomButton, setDisplayRandomButton] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Initialize with empty arrays
  const [selectedGameType, setSelectedGameType] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState([]);
  const [selectedBetType, setSelectedBetType] = useState([]);
  const [selectedAction, setSelectedAction] = useState([]);
  const [selectedStreet, setSelectedStreet] = useState([]);
  const [selectedVillainPosition, setSelectedVillainPosition] = useState([]);
  const [selectedOPENPosition, setSelectedOPENPosition] = useState([]);
  const [selectedCallType, setSelectedCallType] = useState([]);

  // Create random config that follows the same flow as manual configuration
  const createRandomConfig = async () => {
    try {
      // Step 1: Select random hero position
      const heroPosition = ALL_POSITIONS[Math.floor(Math.random() * ALL_POSITIONS.length)];
      console.log('Random config - Selected hero position:', heroPosition);
      
      // Step 2: Get valid bet types for this position
      const validBetTypes = getBetTypes(heroPosition);
      
      // Step 3: Select random valid bet type
      const selectedBetType = validBetTypes[Math.floor(Math.random() * validBetTypes.length)];
      console.log('Random config - Selected bet type:', selectedBetType);
      
      let openPosition = null;
      let threeBetPosition = null;
      let internalBetType = null;
      let callType = null;
      
      // Step 4: Handle each bet type with improved position validation
      switch (selectedBetType) {
        case 'Open':
          internalBetType = VALID_BET_TYPES.OPEN;
          break;
          
        case 'Call':
          // For calls, determine if it's vs open or vs 3bet or limp (SB only)
          // Get valid call types for this position
          const validCallTypes = getValidCallTypes(heroPosition);
          
          // Safety check - if no valid call types (which shouldn't happen), fallback to Open
          if (validCallTypes.length === 0) {
            console.log('No valid call types for position:', heroPosition, '- falling back to Open');
            internalBetType = VALID_BET_TYPES.OPEN;
            break;
          }
          
          callType = validCallTypes[Math.floor(Math.random() * validCallTypes.length)];
          console.log('Selected call type:', callType);
          
          if (callType === 'Limp' && heroPosition === 'SB') {
            internalBetType = VALID_BET_TYPES.SB_LIMP;
          } else           if (callType === 'vs BB 3bet' && heroPosition === 'SB') {
            // For SB calling BB 3bet, automatically set BB as the 3bettor
            threeBetPosition = 'BB';
            internalBetType = VALID_BET_TYPES.FACING_3BET_CALL;
            console.log('SB calling BB 3bet - automatically set threeBetPosition to BB');
          }
          else if (callType === 'vs 3bet') {
            // Get valid 3bet positions for this hero
            const valid3Bettors = getValidOpenPositions(heroPosition, 'Call', 'vs 3bet');
            console.log('Valid 3bettors for', heroPosition, ':', valid3Bettors);
            
            if (valid3Bettors && valid3Bettors.length > 0) {
              threeBetPosition = valid3Bettors[Math.floor(Math.random() * valid3Bettors.length)];
              internalBetType = VALID_BET_TYPES.FACING_3BET_CALL;
              console.log('Selected 3bettor position:', threeBetPosition);
            } else {
              // Fallback to open if no valid 3bettors
              console.log('No valid 3bettors for', heroPosition, '- falling back to Open');
              internalBetType = VALID_BET_TYPES.OPEN;
            }
          } else { // vs Open
            // Get valid open positions based on hero position from position rules
            const validOpeners = POSITION_RULES[heroPosition].canCallOpens;
            console.log('Valid openers for', heroPosition, ':', validOpeners);
            
            if (validOpeners && validOpeners.length > 0) {
              openPosition = validOpeners[Math.floor(Math.random() * validOpeners.length)];
              internalBetType = VALID_BET_TYPES.FACING_OPEN_CALL;
              console.log('Selected opener position:', openPosition);
            } else {
              // Fallback to open if no valid openers
              console.log('No valid openers for', heroPosition, '- falling back to Open');
              internalBetType = VALID_BET_TYPES.OPEN;
            }
          }
          break;
          
        case '3bet':
          // Get valid open positions to 3bet against from position rules
          const valid3betTargets = POSITION_RULES[heroPosition].can3bet;
          if (valid3betTargets.length > 0) {
            openPosition = valid3betTargets[Math.floor(Math.random() * valid3betTargets.length)];
            internalBetType = VALID_BET_TYPES.FACING_OPEN_3BET;
          } else {
            internalBetType = VALID_BET_TYPES.OPEN;
          }
          break;
          
        case '4bet':
          // Get valid 3bet positions to 4bet against from position rules
          const valid4betTargets = POSITION_RULES[heroPosition].can4bet;
          if (valid4betTargets.length > 0) {
            threeBetPosition = valid4betTargets[Math.floor(Math.random() * valid4betTargets.length)];
            internalBetType = VALID_BET_TYPES.FACING_3BET_4BET;
          } else {
            internalBetType = VALID_BET_TYPES.OPEN;
          }
          break;
      }
      
      // Final validation check - hero can never be the same as opponent positions
      if (heroPosition === openPosition || heroPosition === threeBetPosition) {
        console.error('Invalid random config - hero cannot be the same as opponent positions');
        // Recursively try again to get a valid config
        return createRandomConfig();
      }
      
      // Ensure we have all required parameters for the range path
      if (internalBetType === VALID_BET_TYPES.FACING_3BET_CALL && !threeBetPosition && !openPosition) {
        console.error('Missing required 3bettor position for vs 3bet call');
        // Fall back to an open range if we can't get proper parameters
        internalBetType = VALID_BET_TYPES.OPEN;
      }
      
      // Generate range path
      const rangePath = getRangePathMapping(
        heroPosition,
        internalBetType,
        callType,
        openPosition,
        threeBetPosition
      );
      
      // Final safety check - if we couldn't generate a valid range path, fall back to Open
      if (!rangePath) {
        console.error('Failed to generate valid range path - falling back to Open');
        return {
          heroPosition,
          position: heroPosition,
          betType: VALID_BET_TYPES.OPEN,
          openPosition: null,
          threeBetPosition: null,
          isVs3bet: false,
          callType: null,
          originalBetType: 'Open',
          isLimp: false,
          totalHands: 10,
          range: `${'/database/preflop/ranges'}/${heroPosition.toLowerCase()}/${heroPosition.toLowerCase()}_open.csv`
        };
      }
      
      return {
        heroPosition,
        position: heroPosition,
        betType: internalBetType,
        openPosition,
        threeBetPosition,
        isVs3bet: callType === 'vs 3bet',
        callType,
        originalBetType: selectedBetType,
        isLimp: callType === 'Limp',
        totalHands: 10,
        range: rangePath
      };
      
    } catch (error) {
      console.error('Error in random config:', error);
      throw error;
    }
  };

  // Event Handlers
  const handleGameTypeChange = (option) => {
    console.log('Game type selected:', option);
    setSelectedGameType([option]);
    // Hide random button immediately when Pre Flop is selected
    if (option === 'Pre Flop') {
      setDisplayRandomButton(false);
    }
    // Reset all other selections
    setSelectedPosition([]);
    setSelectedBetType([]);
    setSelectedAction([]);
    setSelectedStreet([]);
    setSelectedVillainPosition([]);
    setSelectedOPENPosition([]);
    setSelectedCallType([]);
    // Clear any previous errors
    setError(null);
  };

  const handlePositionChange = (option) => {
    console.log('Position selected:', option);
    setSelectedPosition([option]);
    // Reset dependent selections whenever position changes
    setSelectedBetType([]);
    setSelectedOPENPosition([]);
    setSelectedCallType([]);
    handleAnySelection();
  };
  
  const handleBetTypeChange = (option) => {
    console.log('Bet type selected:', option);
    // Validate bet type before selection
    if (selectedPosition.length === 0) {
      setError('Please select a position first');
      return;
    }
    
    const isValidBetType = validateBetType(selectedPosition[0], option);
    if (!isValidBetType) {
      console.log('Invalid bet type for position:', selectedPosition[0], option);
      setError(`Invalid bet type: ${selectedPosition[0]} cannot ${option.toLowerCase()}`);
      return;
    }
    
    // Add extra validation for BB not being able to 4bet
    if (selectedPosition[0] === 'BB' && option === '4bet') {
      setError('Big Blind (BB) cannot 4bet');
      return;
    }
    
    setSelectedBetType([option]);
    
    // For LJ, HJ, CO - automatically set call type to 'vs 3bet'
    if (option === 'Call' && ['LJ', 'HJ', 'CO'].includes(selectedPosition[0])) {
      setSelectedCallType(['vs 3bet']);
    } else {
      setSelectedCallType([]);
    }
    
    setSelectedOPENPosition([]);
    setError(null);
    handleAnySelection();
  };
  
  const handleCallTypeChange = (option) => {
    console.log('Call type selected:', option);
    setSelectedCallType([option]);
    
    // For SB calling BB 3bet, automatically set the BB as the 3bet position
    if (option === 'vs BB 3bet' && selectedPosition[0] === 'SB') {
      setSelectedOPENPosition(['BB']);
    } else {
      setSelectedOPENPosition([]);
    }
  };
  
  const handleOPENPositionChange = (option) => {
    console.log('OPEN position selected:', option);
    setSelectedOPENPosition([option]);
  };
  
  const handleActionChange = (option) => {
    console.log('Action selected:', option);
    setSelectedAction([option]);
    setSelectedStreet([]);
    setSelectedPosition([]);
    setSelectedVillainPosition([]);
  };
  
  const handleStreetChange = (option) => {
    console.log('Street selected:', option);
    setSelectedStreet([option]);
    setSelectedPosition([]);
    setSelectedVillainPosition([]);
  };
  
  const handleVillainChange = (option) => {
    console.log('Villain position selected:', option);
    setSelectedVillainPosition([option]);
  };

  const handleReset = () => {
    console.log('Resetting all selections');
    setSelectedGameType([]);
    setSelectedPosition([]);
    setSelectedBetType([]);
    setSelectedAction([]);
    setSelectedStreet([]);
    setSelectedVillainPosition([]);
    setSelectedOPENPosition([]);
    setSelectedCallType([]);
    setDisplayRandomButton(true);
    setError(null);
  };

  const handleAnySelection = () => {
    setDisplayRandomButton(false);
  };

  const handleRandomConfig = async () => {
    console.log('Random config button clicked');
    if (isProcessing) {
      console.log('Already processing, ignoring click');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      console.log('Creating random config...');
      const config = await createRandomConfig();
      console.log('Random config created:', config);

      if (config) {
        setDisplayRandomButton(false);
        console.log('Navigating to range screen with config');
        navigate('/range', { state: { config } });
      }
    } catch (error) {
      console.error('Error in random config:', error);
      setDisplayRandomButton(true);
      setError('Failed to create random configuration: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNext = async () => {
    console.log('Next button clicked');
    setIsProcessing(true);
    setError(null);
    
    try {
      const position = selectedPosition[0];
      const betType = selectedBetType[0];
      const selectedOpponentPosition = selectedOPENPosition[0];
      const callType = selectedCallType && selectedCallType.length > 0 ? selectedCallType[0] : null;
      
      console.log('User selections:', { 
        position, 
        betType, 
        selectedOpponentPosition,
        callType 
      });
      
      // Get internal bet type
      let internalBetType;
      
      // Handle SB Limp from Call Type section
      if (betType === 'Call' && callType === 'Limp' && position === 'SB') {
        internalBetType = VALID_BET_TYPES.SB_LIMP;
      } else if (betType === 'Call' && (callType === 'vs 3bet' || callType === 'vs BB 3bet')) {
        internalBetType = VALID_BET_TYPES.FACING_3BET_CALL;
      } else if (betType === 'Call') {
        internalBetType = VALID_BET_TYPES.FACING_OPEN_CALL;
      } else if (betType === '3bet') {
        internalBetType = VALID_BET_TYPES.FACING_OPEN_3BET;
      } else if (betType === '4bet') {
        internalBetType = VALID_BET_TYPES.FACING_3BET_4BET;
      } else if (betType === 'Open') {
        internalBetType = VALID_BET_TYPES.OPEN;
      }
      
      // Set up the positions EXPLICITLY by scenario
      let openPosition = null;
      let threeBetPosition = null;
      
      if (betType === 'Call' && callType === 'Limp' && position === 'SB') {
        // For SB limp, no opponent positions needed
        openPosition = null;
        threeBetPosition = null;
      }
      else if (betType === 'Call' && callType === 'vs BB 3bet' && position === 'SB') {
        // For SB calling BB 3bet, automatically set BB as the 3bettor
        threeBetPosition = 'BB';
      }
      else if (betType === 'Call' && callType === 'vs 3bet') {
        // For 3bet call scenarios, the opponent is the 3bettor
        threeBetPosition = selectedOpponentPosition;
      } 
      else if (betType === 'Call') {
        // For regular calls, selected position is the opener
        openPosition = selectedOpponentPosition;
      } 
      else if (betType === '3bet') {
        // For 3betting, selected position is the opener
        openPosition = selectedOpponentPosition;
      } 
      else if (betType === '4bet') {
        // For 4betting, selected position is the 3bettor
        threeBetPosition = selectedOpponentPosition;
      }
      
      // Generate range path
      const rangePath = getRangePathMapping(
        position,
        internalBetType,
        callType,
        openPosition,
        threeBetPosition
      );
      
      // Create final config
      const config = {
        heroPosition: position,
        position,
        betType: internalBetType,
        openPosition,
        threeBetPosition,
        isVs3bet: callType === 'vs 3bet',
        callType,
        isLimp: callType === 'Limp',
        totalHands: 10,
        range: rangePath
      };
      
      console.log('FINAL CONFIG:', config);
      
      navigate('/range', { state: { config } });
      
    } catch (error) {
      console.error('Error in handleNext:', error);
      setError(error.message || 'Failed to proceed');
    } finally {
      setIsProcessing(false);
    }
  };

  const isConfigComplete = () => {
    // For Pre Flop scenarios
    if (selectedGameType[0] === 'Pre Flop') {
      if (selectedPosition.length === 0) return false;
      if (selectedBetType.length === 0) return false;
      
      // For SB Limp via Call Type, we don't need opponent position
      if (selectedPosition[0] === 'SB' && 
          selectedBetType[0] === 'Call' && 
          selectedCallType.length > 0 && 
          selectedCallType[0] === 'Limp') return true;
      
      // For SB calling BB 3bet, we don't need to select an opponent position
      if (selectedPosition[0] === 'SB' && 
          selectedBetType[0] === 'Call' && 
          selectedCallType.length > 0 && 
          selectedCallType[0] === 'vs BB 3bet') return true;
      
      // For Call, need call type (except for LJ, HJ, CO which are automatically vs 3bet)
      if (selectedBetType[0] === 'Call' && 
          !['LJ', 'HJ', 'CO'].includes(selectedPosition[0]) && 
          selectedCallType.length === 0) return false;
      
      // For 3bet, 4bet, or Call scenarios, need opponent position
      // But not for Limp call type or SB calling BB 3bet
      if ((selectedBetType[0] === '3bet' || 
           selectedBetType[0] === '4bet' || 
           (selectedBetType[0] === 'Call' && 
            (selectedCallType.length > 0 && 
             !['Limp', 'vs BB 3bet'].includes(selectedCallType[0]) || 
             ['LJ', 'HJ', 'CO'].includes(selectedPosition[0])))) && 
          selectedOPENPosition.length === 0) return false;
      
      return true;
    }
    
    // For Post Flop scenarios
    if (selectedGameType[0] === 'Post Flop') {
      if (selectedAction.length === 0) return false;
      if (selectedStreet.length === 0) return false;
      if (selectedPosition.length === 0) return false;
      if (selectedVillainPosition.length === 0) return false;
      
      return true;
    }
    
    return false;
  };

  // ── Scroll gradient + auto-reveal ─────────────────────────────────────────
  // Same fix as BlastConfigScreen.jsx: sections render one at a time as the
  // user completes each prior one, so the scrollable area keeps growing
  // through the whole config flow — not just once the config is complete.
  // The gradient needs to track actual overflow (scrollHeight > clientHeight),
  // not isConfigComplete(), so it shows the moment there's really something
  // to scroll to. Recomputed after every selection change, once the
  // newly-revealed section has actually painted.
  const scrollContainerRef  = useRef(null);
  const prevScrollHeightRef = useRef(null);
  const [canScrollConfig, setCanScrollConfig] = useState(false);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;

    const raf = requestAnimationFrame(() => {
      const newHeight = el.scrollHeight;
      setCanScrollConfig(newHeight > el.clientHeight + 1);

      // Auto-reveal: when a selection just added a new section below the
      // fold, scroll it into view instead of leaving it hidden with only
      // the gradient as a hint some users won't notice. Skipped on the
      // very first measurement (mount) so opening the screen never
      // auto-scrolls on its own — only a genuine, user-caused content
      // increase does.
      if (prevScrollHeightRef.current !== null && newHeight > prevScrollHeightRef.current) {
        el.scrollTo({ top: newHeight, behavior: 'smooth' });
      }
      prevScrollHeightRef.current = newHeight;
    });

    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGameType, selectedPosition, selectedBetType, selectedAction,
      selectedStreet, selectedVillainPosition, selectedOPENPosition, selectedCallType]);

  // Viewport-size changes (rotation, on-screen keyboard, etc.) can flip
  // whether the same content overflows, independent of any selection change.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;
    const recompute = () => setCanScrollConfig(el.scrollHeight > el.clientHeight + 1);
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, []);

  // Debug useEffect for development
  useEffect(() => {
    console.log('Component state updated:', {
      selectedGameType,
      selectedPosition,
      selectedBetType,
      selectedCallType,
      selectedOPENPosition,
      displayRandomButton,
      isProcessing
    });
  }, [selectedGameType, selectedPosition, selectedBetType, selectedCallType, selectedOPENPosition, displayRandomButton, isProcessing]);

  return (
    <div className="quiz-config">
      <div className="quiz-config-content">
        <div className="quiz-config-scroll-wrapper">
          <div
            className="quiz-config-scroll"
            ref={scrollContainerRef}
          >
            {/* Sticky nav + scrollable title — same pattern as BlastConfigScreen */}
            <div className="quiz-config-sticky-nav-container">
              <div className="quiz-config-navigation">
                <button className="nav-link" onClick={() => navigate('/')}>
                  Dashboard
                </button>
                <button className="nav-link" onClick={handleReset}>
                  Reset
                </button>
              </div>
              <div className="title-container">
                <h1 className="quiz-config-title">6max Cash Game</h1>
              </div>
            </div>

        {error && (
          <div className="error-message" style={{ color: 'red', margin: '10px 0' }}>
            {error}
          </div>
        )}

        <SelectionGroup
          title="Focus Type"
          description="Before the flop or on the flop, turn, and river."
          options={GAME_TYPES}
          selectedOptions={selectedGameType}
          onSelect={handleGameTypeChange}
          className="config"
          renderOption={(option) => ({
            label: option,
            className: `select-button ${option === 'Post Flop' ? 'select-button-inactive' : ''}`
          })}
        />

        {selectedGameType[0] === 'Pre Flop' && (
          <>
            <SelectionGroup
              title="Your Position"
              options={ALL_POSITIONS}
              selectedOptions={selectedPosition}
              onSelect={handlePositionChange}
              className="config"
            />

            {selectedPosition.length > 0 && (
              <SelectionGroup
                title="Range Type"
                description="Open is when you're first to act or it's folded to you. Call, 3bet, and 4bet are all reactions to an open."
                options={getBetTypes(selectedPosition[0])}
                selectedOptions={selectedBetType}
                onSelect={handleBetTypeChange}
                className="config"
              />
            )}

            {selectedBetType[0] === 'Call' && 
            !['LJ', 'HJ', 'CO'].includes(selectedPosition[0]) && (
              <SelectionGroup
                title="Call Type"
                options={getValidCallTypes(selectedPosition[0])}
                selectedOptions={selectedCallType}
                onSelect={handleCallTypeChange}
                className="config"
              />
            )}

            {/* Show Position that 3bet/Open section with proper title */}
            {((selectedBetType[0] === '3bet' || selectedBetType[0] === '4bet') || 
              (selectedBetType[0] === 'Call' && (
                selectedCallType.length > 0 && 
                !['Limp', 'vs BB 3bet'].includes(selectedCallType[0]) || 
                ['LJ', 'HJ', 'CO'].includes(selectedPosition[0])
              ))) && (
              <SelectionGroup
                title={
                  selectedBetType[0] === '3bet' ? 'Position that Open' :
                  selectedBetType[0] === '4bet' ? 'Position that 3bet' :
                  ['LJ', 'HJ', 'CO'].includes(selectedPosition[0]) ? 'Position that 3bet' :
                  selectedCallType[0] === 'vs Open' ? 'Position that Open' : 
                  'Position that 3bet'
                }
                options={getValidOpenPositions(selectedPosition[0], selectedBetType[0], 
                                            ['LJ', 'HJ', 'CO'].includes(selectedPosition[0]) ? 'vs 3bet' : selectedCallType[0])}
                selectedOptions={selectedOPENPosition}
                onSelect={handleOPENPositionChange}
                className="config"
              />
            )}
          </>
        )}

        {selectedGameType[0] === 'Post Flop' && (
          <>
            <SelectionGroup
              title="Action"
              options={ACTIONS}
              selectedOptions={selectedAction}
              onSelect={handleActionChange}
              className="config"
            />

            {selectedAction.length > 0 && (
              <SelectionGroup
                title="Street"
                options={STREETS}
                selectedOptions={selectedStreet}
                onSelect={handleStreetChange}
                className="config"
              />
            )}

            {selectedStreet.length > 0 && (
              <SelectionGroup
                title="Your Position"
                options={ALL_POSITIONS}
                selectedOptions={selectedPosition}
                onSelect={handlePositionChange}
                className="config"
              />
            )}

            {selectedPosition.length > 0 && (
              <SelectionGroup
                title="Villain Position"
                options={ALL_POSITIONS}
                selectedOptions={selectedVillainPosition}
                onSelect={handleVillainChange}
                filterOptions={(pos) => pos !== selectedPosition[0]}
                className="config"
              />
            )}
          </>
        )}

            <div className="scroll-bottom-spacer" />
          </div>

          {canScrollConfig && <div className="scroll-gradient" aria-hidden="true" />}
        </div>

        <div className="quiz-config-footer">
          {displayRandomButton && (
            <ButtonLarge
              text={isProcessing ? "Configuring..." : "Configure a Random Quiz"}
              buttonLargeClassName={`button-large random-config-button ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handleRandomConfig}
              disabled={isProcessing}
            />
          )}
          {!displayRandomButton && isConfigComplete() && (
            <ButtonLarge
              text="Next"
              buttonLargeClassName="button-large config-next-button"
              onClick={handleNext}
              disabled={isProcessing}
            />
          )}
        </div>
      </div>
    </div>
  );
}; // End of QuizConfigScreen component

export default QuizConfigScreen;